import { handleFaildToEnterShopError } from '@/entry-limit/error-handling'
import { log } from '@/log'
import ApiBrand from '@/models/api/brand'
import { ApiProduct } from '@/models/api/product'
import {
  defaultAddItemToCartErrorMessage,
  DisplayErrorMessage,
  parseAddItemValidationError,
} from '@/models/cart/error/error-response-parser'
import { isPaidMembershipPrice, SkuPrice } from '@/models/item/sku'
import { AuthenticationService } from '@/services/auth/authentication'
import { CartService } from '@ajax/modules/services/cart'
import { ProductService as AjaxProductService } from '@ajax/modules/services/product'
import { Either, Left, Right } from 'fp-ts/lib/Either'

export interface NewCartItem {
  readonly productId: string,
  readonly count: number,
  readonly price: SkuPrice
}

export default class AddItemToCart {
  constructor(
    private cartService: CartService,
    private ajaxProductService: AjaxProductService
  ) {}

  /**
   * @return
   * カート追加に失敗した場合にはエラーメッセージとして{@link string}を返す。
   */
  async execute(items: NewCartItem[]): Promise<Either<DisplayErrorMessage, string[]>>
  async execute(items: NewCartItem): Promise<Either<DisplayErrorMessage, string>>
  async execute(items: NewCartItem | NewCartItem[]): Promise<Either<DisplayErrorMessage, string | string[]>> {
    if (!Array.isArray(items)) {
      return this.execute([items])
    }

    const authService = AuthenticationService.create()
    const resultRegistrerProvisional = await authService.registerAsProvisilnalIf(loginState => loginState.isGuest())
    if (resultRegistrerProvisional.isLeft()) {
      log.error(resultRegistrerProvisional.value)

      return new Left(defaultAddItemToCartErrorMessage)
    }

    const result = await this.ajaxProductService.commonItemList({
      with: 'stock~detail',
      ids: items.map(i => i.productId).join(','),
    })
    if (result.isLeft()) {
      return new Left(defaultAddItemToCartErrorMessage)
    }

    const products = result.value
    if (!allProductsBelongingToBrand(products)) {
      return new Left(defaultAddItemToCartErrorMessage)
    }

    const postResult = await this.cartService.post(items.map(i => ({
      productId: i.productId,
      unitPrice: i.price.prices.selected.price,
      isPaidMembershipPrice: isPaidMembershipPrice(i.price),
      unitCount: i.count,
    })))

    if (postResult.isLeft()) {
      const response = (postResult.value as any).response

      log.error({ response })

      handleFaildToEnterShopError(response)

      return new Left(parseAddItemValidationError(response))
    }

    return new Right(products.map(p => p.belongingBrand.englishName))
  }
}

type ProductWithBrand = ApiProduct & {
  belongingBrand: ApiBrand
}
function allProductsBelongingToBrand(products: ApiProduct[]): products is ProductWithBrand[] {
  return products.every(p => p.belongingBrand !== null)
}
